
import React, { useState } from 'react';
import type { StaffMember, Department, DayOfWeek, AvailabilityStatus } from '../../types';
import Modal from '../ui/Modal';
import { Icons } from '../ui/Icons';
import AvailabilityCalendar from './AvailabilityCalendar';

interface StaffModalProps {
    staff: StaffMember | null;
    onClose: () => void;
    onSave: (staff: StaffMember) => void;
    departments: Department[];
    weekStartDay: DayOfWeek;
}

    const emptyStaff: Omit<StaffMember, 'id'> = {
    name: '',
    color: '#3b82f6',
    email: '',
    phone: '',
    role: '',
    notes: '',
    departmentId: null,
    isDriver: false,
    password: '',
    availability: {
        Monday: 'Available',
        Tuesday: 'Available',
        Wednesday: 'Available',
        Thursday: 'Available',
        Friday: 'Available',
        Saturday: 'Available',
        Sunday: 'Available',
    },
    availabilityOverrides: {},
};

const StaffModal: React.FC<StaffModalProps> = ({ staff, onClose, onSave, departments, weekStartDay }) => {
    const [activeTab, setActiveTab] = useState<'profile' | 'contact' | 'availability'>('profile');
    const [formData, setFormData] = useState<StaffMember>(staff ? { ...staff } : { ...emptyStaff, id: '' });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target as HTMLInputElement;
        setFormData({ 
            ...formData, 
            [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value 
        });
    };

    const handleOverridesChange = (overrides: { [date: string]: AvailabilityStatus; }) => {
        setFormData(prev => ({ ...prev, availabilityOverrides: overrides }));
    };

    const handleDefaultAvailabilityChange = (day: DayOfWeek, status: AvailabilityStatus) => {
        setFormData(prev => ({
            ...prev,
            availability: { ...prev.availability, [day]: status }
        }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave(formData);
    };
    
    const renderProfileTab = () => (
        <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Full Name</label>
                    <input type="text" name="name" id="name" value={formData.name} onChange={handleChange} required className="mt-1 block w-full border border-gray-300 dark:border-slate-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-200" />
                </div>
                 <div>
                    <label htmlFor="role" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Role</label>
                    <input type="text" name="role" id="role" value={formData.role} onChange={handleChange} required className="mt-1 block w-full border border-gray-300 dark:border-slate-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-200" />
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label htmlFor="departmentId" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Department</label>
                    <select name="departmentId" id="departmentId" value={formData.departmentId || ''} onChange={handleChange} className="mt-1 block w-full border border-gray-300 dark:border-slate-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-200">
                        <option value="">Select a department</option>
                        {departments.map(dep => <option key={dep.id} value={dep.id}>{dep.name}</option>)}
                    </select>
                </div>
                <div>
                    <label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
                    <select name="status" id="status" value={formData.status || 'active'} onChange={handleChange} className="mt-1 block w-full border border-gray-300 dark:border-slate-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-550 sm:text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-200">
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                    </select>
                </div>
            </div>
            <div className="flex items-center gap-2 py-2">
                <input
                    type="checkbox"
                    id="isDriver"
                    name="isDriver"
                    checked={formData.isDriver || false}
                    onChange={handleChange}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="isDriver" className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                    <Icons.Bus className="w-4 h-4 text-indigo-500" />
                    This staff member is a Driver
                </label>
            </div>
             <div>
                <label htmlFor="color" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Display Color</label>
                <input type="color" name="color" id="color" value={formData.color} onChange={handleChange} className="mt-1 block w-full h-10 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm" />
            </div>
             <div>
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Notes</label>
                <textarea name="notes" id="notes" value={formData.notes} onChange={handleChange} rows={3} className="mt-1 block w-full border border-gray-300 dark:border-slate-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-200"></textarea>
            </div>
        </div>
    );

    const renderContactTab = () => (
        <div className="space-y-4">
            <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email Address</label>
                <input type="email" name="email" id="email" value={formData.email} onChange={handleChange} required className="mt-1 block w-full border border-gray-300 dark:border-slate-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-200" />
            </div>
            <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Phone Number</label>
                <input type="tel" name="phone" id="phone" value={formData.phone} onChange={handleChange} className="mt-1 block w-full border border-gray-300 dark:border-slate-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-200" />
            </div>
            <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Staff Portal Password</label>
                <input type="text" name="password" id="password" value={formData.password || ''} onChange={handleChange} placeholder="Set a password for staff login" className="mt-1 block w-full border border-gray-300 dark:border-slate-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-200" />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">This password is for the staff-only view, not the admin dashboard.</p>
            </div>
        </div>
    );

    const renderAvailabilityTab = () => (
       // FIX: The AvailabilityCalendar component was called without the required 'variant' prop. It's now split into two calls, one for 'overrides' and one for 'defaults', to provide full editing capability within the tab.
       <div>
           <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-2">Date-Specific Overrides</h4>
           <AvailabilityCalendar
                availability={formData.availability}
                availabilityOverrides={formData.availabilityOverrides || {}}
                weekStartDay={weekStartDay}
                onOverridesChange={handleOverridesChange}
                onAvailabilityChange={handleDefaultAvailabilityChange}
                variant="overrides"
           />
           <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mt-6 mb-2">Default Weekly Schedule</h4>
           <AvailabilityCalendar
                availability={formData.availability}
                availabilityOverrides={formData.availabilityOverrides || {}}
                weekStartDay={weekStartDay}
                onOverridesChange={handleOverridesChange}
                onAvailabilityChange={handleDefaultAvailabilityChange}
                variant="defaults"
           />
       </div>
    );

    const tabs = [
        { id: 'profile', label: 'Profile', icon: <Icons.User className="w-5 h-5"/> },
        { id: 'contact', label: 'Contact & Access', icon: <Icons.Mail className="w-5 h-5"/> },
        { id: 'availability', label: 'Availability', icon: <Icons.CalendarCheck className="w-5 h-5"/> },
    ];

    return (
        <Modal title={staff ? 'Edit Staff Member' : 'Add New Staff Member'} onClose={onClose}>
            <form onSubmit={handleSubmit}>
                <div className="p-6">
                    <div className="border-b border-gray-200 dark:border-slate-700 mb-6">
                        <nav className="-mb-px flex space-x-6" aria-label="Tabs">
                            {tabs.map(tab => (
                                <button
                                    type="button"
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id as any)}
                                    className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                                        activeTab === tab.id
                                            ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                                            : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-slate-600'
                                    }`}
                                >
                                    {tab.icon} {tab.label}
                                </button>
                            ))}
                        </nav>
                    </div>
                    {activeTab === 'profile' && renderProfileTab()}
                    {activeTab === 'contact' && renderContactTab()}
                    {activeTab === 'availability' && renderAvailabilityTab()}
                </div>
                <div className="bg-gray-50 dark:bg-slate-900/50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t dark:border-slate-700">
                    <button type="submit" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-slate-800 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm">
                        Save
                    </button>
                    <button type="button" onClick={onClose} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-slate-600 shadow-sm px-4 py-2 bg-white dark:bg-slate-700 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-slate-800 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm">
                        Cancel
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default StaffModal;
